import React, { useState } from 'react';
import { Send } from 'lucide-react';

import { useChat } from '../../context/ChatContext';
import { useToast } from '../ui/Toast';

const describeChatError = (err) => {
  const data = err?.response?.data;
  if (typeof data === 'string') return data;
  if (data && typeof data === 'object') {
    // 'code' (e.g. 'RATE_LIMITED') is a machine-readable identifier, never the
    // human-facing copy — skip it so it can sit anywhere in the error shape
    // without becoming the Toast text.
    const first = Object.entries(data).find(([key, value]) => key !== 'code' && typeof value === 'string');
    if (first) return first[1];
  }
  return 'Something went wrong sending your message. Please try again.';
};

export default function ChatInput() {
  const { sendMessage, sending, dailyLimitReached, initializing } = useChat();
  const { addToast } = useToast();
  const [value, setValue] = useState('');

  const disabled = sending || dailyLimitReached || initializing;

  const submit = async (e) => {
    e.preventDefault();
    const content = value.trim();
    if (!content || disabled) return;

    setValue('');
    try {
      await sendMessage(content);
    } catch (err) {
      addToast(describeChatError(err), { variant: 'error' });
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit(e);
    }
  };

  return (
    <form className="ss-chat-input" onSubmit={submit}>
      <textarea
        className="ss-chat-input__field"
        rows={1}
        placeholder={dailyLimitReached ? "You've reached today's message limit" : 'Ask a question…'}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={disabled}
        aria-label="Message"
      />
      <button
        type="submit"
        className="ss-chat-input__send"
        disabled={disabled || !value.trim()}
        aria-label="Send message"
      >
        <Send size={18} aria-hidden="true" />
      </button>
    </form>
  );
}
